import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Loader2, Upload, ExternalLink, X, ScrollText } from "lucide-react";

const CATS = ["Traditions", "Festivals", "Astrology", "Stories"];
const EMPTY = {
  title: "", topic: "", category: "Traditions", image: "", type_tags: [], author: "Pavitra Mantra Editorial Team",
  author_avatar: "", date: "", read_time: "5 min", excerpt: "", intro: "", content: "",
  gallery: [], mantra: { sanskrit: "", transliteration: "", meaning: "" }, story: { image: "", heading: "", text: "" },
  significance: "", rituals: [], steps: [], hashtags: [],
};

const readImage = (file) =>
  new Promise((resolve, reject) => {
    if (!file) return resolve("");
    if (!file.type.startsWith("image/")) return reject(new Error("Please choose an image"));
    if (file.size > 3 * 1024 * 1024) return reject(new Error("Image must be under 3MB"));
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error("Could not read image"));
    r.readAsDataURL(file);
  });

function ImageInput({ value, onChange, testid, label }) {
  const upload = async (file) => {
    try { const data = await readImage(file); onChange(data); toast.success(`${label} uploaded`); }
    catch (e) { toast.error(e.message); }
  };
  return (
    <div>
      {label && <Label className="mb-1.5 block text-sm">{label}</Label>}
      <div className="flex items-center gap-3">
        {value ? <img src={value} alt="" className="h-14 w-14 rounded-lg object-cover border border-border" /> : <div className="h-14 w-14 rounded-lg border border-dashed border-border grid place-items-center text-muted-foreground"><Upload className="h-4 w-4" /></div>}
        <div className="flex-1 space-y-1.5">
          <Input data-testid={testid} value={value?.startsWith("data:") ? "" : value} placeholder="Paste image URL or upload →" onChange={(e) => onChange(e.target.value)} className="rounded-xl h-9" />
          <div className="flex items-center gap-2">
            <label className="text-xs text-primary cursor-pointer hover:underline flex items-center gap-1"><Upload className="h-3 w-3" /> Upload<input type="file" accept="image/*" data-testid={`${testid}-file`} onChange={(e) => upload(e.target.files[0])} className="hidden" /></label>
            {value && <button type="button" onClick={() => onChange("")} className="text-xs text-destructive hover:underline flex items-center gap-1"><X className="h-3 w-3" /> Clear</button>}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function BlogCMS() {
  const [blogs, setBlogs] = useState([]);
  const [editing, setEditing] = useState(null); // null=list, {} or blog=editor
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/blogs").then((r) => setBlogs(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ ...EMPTY }); setEditing({}); };
  const openEdit = (b) => {
    setForm({
      ...EMPTY, ...b,
      mantra: b.mantra || { sanskrit: "", transliteration: "", meaning: "" },
      story: b.story || { image: "", heading: "", text: "" },
      type_tags: b.type_tags || [], hashtags: b.hashtags || [], rituals: b.rituals || [], gallery: b.gallery || [], steps: b.steps || [],
    });
    setEditing(b);
  };
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        type_tags: form.type_tags.filter(Boolean),
        hashtags: form.hashtags.filter(Boolean),
        rituals: form.rituals.filter((r) => r && r.trim()),
        gallery: form.gallery.filter((g) => g.image),
        steps: form.steps.filter((s) => s.title || s.text),
      };
      if (editing.id) { await api.put(`/admin/blogs/${editing.id}`, payload); toast.success("Article updated"); }
      else { await api.post("/admin/blogs", payload); toast.success("Article published"); }
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save article"); }
    finally { setSaving(false); }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete "${b.title}"? This cannot be undone.`)) return;
    try { await api.delete(`/admin/blogs/${b.id}`); toast.success("Article deleted"); load(); }
    catch { toast.error("Could not delete"); }
  };

  // ---- List view ----
  if (!editing) {
    return (
      <div data-testid="blog-cms">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="font-serif text-xl font-semibold">Vedic Blog articles</h3>
            <p className="text-sm text-muted-foreground">Create and manage rich Vedic articles published by the editorial team.</p>
          </div>
          <Button onClick={openNew} data-testid="new-blog-btn" className="rounded-full bg-primary text-primary-foreground"><Plus className="h-4 w-4 mr-1.5" /> New article</Button>
        </div>
        <div className="space-y-3">
          {blogs.length === 0 && <p className="text-muted-foreground">No articles yet.</p>}
          {blogs.map((b) => (
            <div key={b.id} data-testid={`blog-row-${b.id}`} className="rounded-2xl border border-border bg-card p-4 flex items-center gap-4">
              <img src={b.image} alt="" className="h-14 w-20 rounded-lg object-cover shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-serif font-semibold truncate">{b.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{b.category} · {b.read_time} · {new Date(b.date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })} · <span className="font-mono">/{b.slug}</span></p>
              </div>
              <a href={`/blog/${b.slug}`} target="_blank" rel="noreferrer" data-testid={`view-blog-${b.id}`} className="text-muted-foreground hover:text-primary p-2"><ExternalLink className="h-4 w-4" /></a>
              <Button size="sm" variant="outline" data-testid={`edit-blog-${b.id}`} onClick={() => openEdit(b)} className="rounded-full"><Pencil className="h-4 w-4 mr-1" /> Edit</Button>
              <Button size="sm" variant="outline" data-testid={`delete-blog-${b.id}`} onClick={() => remove(b)} className="rounded-full text-destructive border-destructive/40"><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ---- Editor view ----
  const Field = ({ label, children }) => (<div><Label className="mb-1.5 block">{label}</Label>{children}</div>);
  return (
    <div data-testid="blog-editor" className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="font-serif text-xl font-semibold">{editing.id ? "Edit article" : "New article"}</h3>
        <Button variant="ghost" onClick={() => setEditing(null)} data-testid="blog-editor-cancel" className="rounded-full">Back to list</Button>
      </div>

      {/* Basics */}
      <div className="rounded-2xl border border-border bg-card p-5 space-y-4">
        <Field label="Topic (eyebrow)"><Input data-testid="blog-f-topic" value={form.topic} onChange={(e) => set({ topic: e.target.value })} className="rounded-xl" placeholder="e.g. Shiva Worship" /></Field>
        <Field label="Main heading / Title *"><Input data-testid="blog-f-title" value={form.title} onChange={(e) => set({ title: e.target.value })} className="rounded-xl" /></Field>
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Category">
            <select data-testid="blog-f-category" value={form.category} onChange={(e) => set({ category: e.target.value })} className="w-full h-10 rounded-xl border border-border bg-background px-3 text-sm">{CATS.map((c) => <option key={c}>{c}</option>)}</select>
          </Field>
          <Field label="Reading time"><Input data-testid="blog-f-readtime" value={form.read_time} onChange={(e) => set({ read_time: e.target.value })} className="rounded-xl" placeholder="6 min" /></Field>
          <Field label="Publish date"><Input type="date" data-testid="blog-f-date" value={form.date} onChange={(e) => set({ date: e.target.value })} className="rounded-xl" /></Field>
        </div>
        <Field label="Type tags (comma separated)"><Input data-testid="blog-f-typetags" value={form.type_tags.join(", ")} onChange={(e) => set({ type_tags: e.target.value.split(",").map((s) => s.trim()) })} className="rounded-xl" placeholder="Puja Vidhi, Lord Shiva" /></Field>
        <ImageInput label="Hero image" value={form.image} onChange={(v) => set({ image: v })} testid="blog-f-image" />
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Author"><Input data-testid="blog-f-author" value={form.author} onChange={(e) => set({ author: e.target.value })} className="rounded-xl" /></Field>
          <ImageInput label="Author avatar" value={form.author_avatar} onChange={(v) => set({ author_avatar: v })} testid="blog-f-avatar" />
        </div>
        <Field label="Short excerpt (for cards)"><Textarea data-testid="blog-f-excerpt" value={form.excerpt} onChange={(e) => set({ excerpt: e.target.value })} className="rounded-xl min-h-[60px]" /></Field>
        <Field label="Short introduction (lead)"><Textarea data-testid="blog-f-intro" value={form.intro} onChange={(e) => set({ intro: e.target.value })} className="rounded-xl min-h-[70px]" /></Field>
      </div>

      {/* Gallery */}
      <div className="rounded-2xl border border-border bg-card p-5">
        <div className="flex items-center justify-between mb-3"><h4 className="font-serif font-semibold">Image gallery</h4><Button size="sm" variant="outline" data-testid="add-gallery-btn" onClick={() => set({ gallery: [...form.gallery, { image: "", caption: "" }] })} className="rounded-full"><Plus className="h-4 w-4 mr-1" /> Add image</Button></div>
        <div className="space-y-3">
          {form.gallery.map((g, i) => (
            <div key={i} data-testid={`gallery-item-${i}`} className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end rounded-xl border border-border p-3">
              <ImageInput value={g.image} onChange={(v) => set({ gallery: form.gallery.map((x, idx) => idx === i ? { ...x, image: v } : x) })} testid={`gallery-image-${i}`} label="Image" />
              <Field label="Caption"><Input data-testid={`gallery-caption-${i}`} value={g.caption} onChange={(e) => set({ gallery: form.gallery.map((x, idx) => idx === i ? { ...x, caption: e.target.value } : x) })} className="rounded-xl" /></Field>
              <Button variant="ghost" size="icon" data-testid={`gallery-remove-${i}`} onClick={() => set({ gallery: form.gallery.filter((_, idx) => idx !== i) })} className="rounded-full text-destructive"><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
      </div>

      {/* Mantra */}
      <div className="rounded-2xl border border-border bg-card p-5 space-y-4">
        <h4 className="font-serif font-semibold">Mantra card</h4>
        <Field label="Sanskrit (Devanagari)"><Textarea data-testid="blog-f-mantra-sanskrit" value={form.mantra.sanskrit} onChange={(e) => set({ mantra: { ...form.mantra, sanskrit: e.target.value } })} className="rounded-xl min-h-[60px] font-devanagari" /></Field>
        <Field label="Transliteration"><Input data-testid="blog-f-mantra-translit" value={form.mantra.transliteration} onChange={(e) => set({ mantra: { ...form.mantra, transliteration: e.target.value } })} className="rounded-xl" /></Field>
        <Field label="Meaning"><Textarea data-testid="blog-f-mantra-meaning" value={form.mantra.meaning} onChange={(e) => set({ mantra: { ...form.mantra, meaning: e.target.value } })} className="rounded-xl min-h-[60px]" /></Field>
      </div>

      {/* Main content */}
      <div className="rounded-2xl border border-border bg-card p-5 space-y-4">
        <Field label="Main article content (separate paragraphs with a blank line)"><Textarea data-testid="blog-f-content" value={form.content} onChange={(e) => set({ content: e.target.value })} className="rounded-xl min-h-[160px]" /></Field>
      </div>

      {/* Story */}
      <div className="rounded-2xl border border-border bg-card p-5 space-y-4">
        <h4 className="font-serif font-semibold">Visual storytelling (image + text)</h4>
        <ImageInput label="Story image" value={form.story.image} onChange={(v) => set({ story: { ...form.story, image: v } })} testid="blog-f-story-image" />
        <Field label="Story heading"><Input data-testid="blog-f-story-heading" value={form.story.heading} onChange={(e) => set({ story: { ...form.story, heading: e.target.value } })} className="rounded-xl" /></Field>
        <Field label="Story text"><Textarea data-testid="blog-f-story-text" value={form.story.text} onChange={(e) => set({ story: { ...form.story, text: e.target.value } })} className="rounded-xl min-h-[90px]" /></Field>
      </div>

      {/* Significance */}
      <div className="rounded-2xl border border-border bg-card p-5">
        <Field label="Religious & Spiritual Significance"><Textarea data-testid="blog-f-significance" value={form.significance} onChange={(e) => set({ significance: e.target.value })} className="rounded-xl min-h-[110px]" /></Field>
      </div>

      {/* Rituals */}
      <div className="rounded-2xl border border-border bg-card p-5">
        <Field label="Rituals & Customs (one per line)"><Textarea data-testid="blog-f-rituals" value={form.rituals.join("\n")} onChange={(e) => set({ rituals: e.target.value.split("\n") })} className="rounded-xl min-h-[110px]" placeholder={"Wake before sunrise…\nPrepare panchamrit…"} /></Field>
      </div>

      {/* Steps */}
      <div className="rounded-2xl border border-border bg-card p-5">
        <div className="flex items-center justify-between mb-3"><h4 className="font-serif font-semibold">How it works — steps</h4><Button size="sm" variant="outline" data-testid="add-step-btn" onClick={() => set({ steps: [...form.steps, { title: "", text: "" }] })} className="rounded-full"><Plus className="h-4 w-4 mr-1" /> Add step</Button></div>
        <div className="space-y-3">
          {form.steps.map((s, i) => (
            <div key={i} data-testid={`step-item-${i}`} className="grid sm:grid-cols-[200px_1fr_auto] gap-3 items-end rounded-xl border border-border p-3">
              <Field label={`Step ${i + 1} title`}><Input data-testid={`step-title-${i}`} value={s.title} onChange={(e) => set({ steps: form.steps.map((x, idx) => idx === i ? { ...x, title: e.target.value } : x) })} className="rounded-xl" /></Field>
              <Field label="Description"><Input data-testid={`step-text-${i}`} value={s.text} onChange={(e) => set({ steps: form.steps.map((x, idx) => idx === i ? { ...x, text: e.target.value } : x) })} className="rounded-xl" /></Field>
              <Button variant="ghost" size="icon" data-testid={`step-remove-${i}`} onClick={() => set({ steps: form.steps.filter((_, idx) => idx !== i) })} className="rounded-full text-destructive"><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
      </div>

      {/* Hashtags */}
      <div className="rounded-2xl border border-border bg-card p-5">
        <Field label="Hashtags (comma separated, without #)"><Input data-testid="blog-f-hashtags" value={form.hashtags.join(", ")} onChange={(e) => set({ hashtags: e.target.value.split(",").map((s) => s.trim()) })} className="rounded-xl" placeholder="spiritual, Religious, Shiva" /></Field>
      </div>

      <div className="flex gap-3">
        <Button onClick={save} disabled={saving} data-testid="save-blog-btn" className="rounded-full bg-primary text-primary-foreground px-8 h-11">
          {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</> : <><ScrollText className="h-4 w-4 mr-2" /> {editing.id ? "Update article" : "Publish article"}</>}
        </Button>
        <Button variant="outline" onClick={() => setEditing(null)} className="rounded-full">Cancel</Button>
      </div>
    </div>
  );
}
